/**
 * Self-contained placeholder imagery for the storefront previews.
 *
 * Inline SVG data URIs — no network, no third-party stock service, nothing to
 * upload. Flat fills only (brand §6 bans gradients), drawn as simple garment
 * silhouettes and abstract banner compositions so a preview reads like a
 * dressed shop window instead of a wall of empty texture blocks.
 *
 * **Render-side only, never persisted.** Spec 42 R6 rejects any saved image
 * that isn't under the company's storage prefix, and the template constants
 * are deliberately image-free so their first save passes. These URIs are
 * injected by `withSampleData` at render time and by `PreviewCard` — the same
 * layer that invents the dummy products — so nothing here can ever reach a
 * `store_home_versions` row.
 */

const svgUri = (w: number, h: number, body: string): string =>
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}">${body}</svg>`,
  );

/** Soft flat-lay backgrounds the garments sit on — muted so any theme carries them. */
const TILE_BGS = ["#efe9e1", "#e6edee", "#ece7f0", "#e9efe4", "#f0e9e2"] as const;

/** Faint contact shadow under each garment, so it reads as laid flat. */
const shadow = (cx: number, cy: number, rx: number) =>
  `<ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="18" fill="#000" opacity="0.06"/>`;

const GARMENT = "#ffffff";
const SEAM = "#00000018";

/** Five garment silhouettes, cycled across product tiles and gallery slots. */
const GARMENTS: string[] = [
  // Crew tee
  `${shadow(200, 420, 130)}<path d="M150 120 L98 152 L66 226 L124 252 L140 214 L140 408 L260 408 L260 214 L276 252 L334 226 L302 152 L250 120 C234 146 166 146 150 120 Z" fill="${GARMENT}" stroke="${SEAM}" stroke-width="3"/><path d="M162 124 C176 142 224 142 238 124" fill="none" stroke="${SEAM}" stroke-width="4"/>`,
  // Button-down shirt
  `${shadow(200, 430, 135)}<path d="M144 112 L92 148 L64 230 L120 254 L136 212 L136 418 L264 418 L264 212 L280 254 L336 230 L308 148 L256 112 L232 130 L200 150 L168 130 Z" fill="${GARMENT}" stroke="${SEAM}" stroke-width="3"/><path d="M200 150 L200 414" stroke="${SEAM}" stroke-width="3"/><circle cx="200" cy="190" r="4" fill="${SEAM}"/><circle cx="200" cy="240" r="4" fill="${SEAM}"/><circle cx="200" cy="290" r="4" fill="${SEAM}"/><circle cx="200" cy="340" r="4" fill="${SEAM}"/><path d="M168 130 L184 112 L200 150 L216 112 L232 130" fill="none" stroke="${SEAM}" stroke-width="3"/>`,
  // Hoodie
  `${shadow(200, 430, 140)}<path d="M146 128 L94 160 L68 238 L126 262 L142 222 L142 420 L258 420 L258 222 L274 262 L332 238 L306 160 L254 128 Z" fill="${GARMENT}" stroke="${SEAM}" stroke-width="3"/><path d="M154 132 C150 96 250 96 246 132 C232 152 168 152 154 132 Z" fill="${GARMENT}" stroke="${SEAM}" stroke-width="4"/><path d="M164 330 L164 396 L236 396 L236 330 Z" fill="none" stroke="${SEAM}" stroke-width="3"/><path d="M186 152 L186 190 M214 152 L214 190" stroke="${SEAM}" stroke-width="3"/>`,
  // Trousers
  `${shadow(200, 440, 110)}<path d="M136 96 L264 96 L276 428 L216 428 L202 220 L198 220 L184 428 L124 428 Z" fill="${GARMENT}" stroke="${SEAM}" stroke-width="3"/><path d="M136 128 L264 128" stroke="${SEAM}" stroke-width="3"/><path d="M196 96 L196 122" stroke="${SEAM}" stroke-width="3"/>`,
  // A-line kurti
  `${shadow(200, 440, 130)}<path d="M154 108 L108 140 L84 210 L134 232 L146 200 L118 428 L282 428 L254 200 L266 232 L316 210 L292 140 L246 108 C232 130 168 130 154 108 Z" fill="${GARMENT}" stroke="${SEAM}" stroke-width="3"/><path d="M168 112 C180 128 220 128 232 112" fill="none" stroke="${SEAM}" stroke-width="4"/><path d="M160 260 L240 260" stroke="${SEAM}" stroke-width="3"/>`,
];

/**
 * The garment-silhouette SVG for slot `i` — the offline fallback behind the
 * photographs below, swapped in by `PreviewCard` when a photo fails to load.
 */
export function sampleProductFallback(i: number): string {
  const bg = TILE_BGS[i % TILE_BGS.length]!;
  const garment = GARMENTS[i % GARMENTS.length]!;
  return svgUri(400, 500, `<rect width="400" height="500" fill="${bg}"/>${garment}`);
}

// ── Real photography ───────────────────────────────────────────────
//
// Curated apparel photos hotlinked from Unsplash (Unsplash License: free for
// commercial use, no attribution required). Every id below was verified to
// resolve and eyeballed to actually be apparel before landing here. Sizing is
// done by Unsplash's CDN (`w`/`h`/`fit=crop`), so the browser downloads
// tile-sized files, not originals. If the CDN is unreachable the product tiles
// fall back to the silhouettes above; the packaged hero/gallery components have
// no error hook, so those slots degrade to a blank band offline — acceptable
// for a dev/preview surface.

const unsplash = (id: string, w: number, h: number): string =>
  `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&h=${h}&q=70`;

/** Tile photos, index-aligned with `SAMPLE_LISTINGS` in composer-preview. */
const PRODUCT_PHOTOS = [
  "1521572163474-6864f9cf17ab", // white crew tee on model
  "1596755094514-f87e34085b2c", // chambray shirt on hanger
  "1562157873-818bc0726f68", // folded colourful tees, flat lay
  "1541099649105-f69ad21f3246", // jeans, street
  "1556905055-8f358a7a47b2", // cosy knit flat lay
  "1620799140408-edc6dcb6d633", // white sweatshirt, flat
  "1523381210434-271e8be1f52b", // sage tees on wooden hangers
  "1591047139829-d91aecb6caea", // tan bomber jacket on hanger
  "1618354691373-d851c5c3a990", // black tee on hanger
  "1558769132-cb1aea458c5e", // knitwear rail
] as const;

/** A product-tile image — real apparel photography, cycled per slot. */
export function sampleProductImage(i: number): string {
  return unsplash(PRODUCT_PHOTOS[i % PRODUCT_PHOTOS.length]!, 480, 600);
}

/**
 * Hero banners — real shop and rail scenes, wide-cropped. The hero paints
 * white text with its own scrim over these, so busy areas stay legible.
 * Cycled across slides so a multi-slide hero shows variety.
 */
export const SAMPLE_HERO_IMAGES: string[] = [
  unsplash("1441986300917-64674bd600d8", 1600, 600), // apparel shop, racks + shelves
  unsplash("1489987707025-afc232f7ea0f", 1600, 600), // colourful shirts on a rail
  unsplash("1567401893414-76b7b1e5a7a5", 1600, 600), // boutique rail with dresses
];

/** A side-image for image+text blocks — a knitwear rail, calm and neutral. */
export const SAMPLE_FABRIC_IMAGE: string = unsplash("1558769132-cb1aea458c5e", 800, 600);
